import React, { useState, useCallback } from "react";
import {
  Box,
  Button,
  TextField,
  Typography,
  Paper,
  Grid,
  Snackbar,
  Alert,
} from "@mui/material";
import { httpsCallable } from "firebase/functions";
import { functions } from "../firebase";

function CardManager() {
  // 1) function declarations at top level of the component
  const convertToDatetimeLocal = (str) => {
    if (!str) return "";
    // e.g. "2023-01-01 12:00:00" => "2023-01-01T12:00"
    const parts = str.split(" ");
    if (parts.length === 2) {
      const [date, time] = parts;
      const [hh, mm] = time.split(":");
      return `${date}T${hh}:${mm}`; 
    }
    if (/^\d{4}-\d{2}-\d{2}$/.test(str)) {
      return `${str}T00:00`;
    }
    return str;
  };

  const convertFromDatetimeLocal = (dtLocal) => {
    if (!dtLocal) return "";
    // e.g. "2023-02-27T15:30"
    const [date, time] = dtLocal.split("T");
    const [hh, mm] = time.split(":");
    return `${date} ${hh}:${mm}:00`;
  };

  // State + snackbars + callEndpoint, etc
  const [lookupCardNumber, setLookupCardNumber] = useState("");
  const [cardData, setCardData] = useState({
    cardNumber: "",
    name: "",
    validFrom: "",
    validTo: "",
    pin: "",
    doorFlags: "0 0 0 0",
  });

  const [result, setResult] = useState("");
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");

  const showSnackbar = useCallback((message) => {
    setSnackbarMessage(message);
    setSnackbarOpen(true);
  }, []);

  const handleSnackbarClose = useCallback(() => {
    setSnackbarOpen(false);
  }, []);

  const callEndpoint = useCallback(
    async (fnName, params = {}) => {
      try {
        const fn = httpsCallable(functions, fnName);
        const response = await fn(params);
        return response.data;
      } catch (error) {
        console.error("Error calling endpoint:", error);
        showSnackbar(`Error: ${error.message}`);
        throw error;
      }
    },
    [showSnackbar]
  );

  // 2) handleFetchCard
  const handleFetchCard = useCallback(async () => {
    if (!lookupCardNumber) {
      showSnackbar("Please enter a card number to fetch.");
      return;
    }
    try {
      // a) getCard
      const cardResp = await callEndpoint("getCard", {
        cardNumber: parseInt(lookupCardNumber, 10),
      });

      if (!cardResp.successfull || !cardResp.value) {
        setResult(JSON.stringify(cardResp, null, 2));
        showSnackbar("Failed to get card info");
        return;
      }

      // b) readNameByCard
      const nameResp = await callEndpoint("readNameByCard", {
        cardNumber: parseInt(lookupCardNumber, 10),
      });

      // Extract fields
      const { number, valid, doors, pin } = cardResp.value ?? {};
      const cardNumberVal = number ?? 0;
      const validObj = valid ?? { from: "", to: "" };
      const pinVal = pin ?? 0;
      const doorsObj = doors ?? { "1": false, "2": false, "3": false, "4": false };

      const doorFlagsStr = [doorsObj["1"], doorsObj["2"], doorsObj["3"], doorsObj["4"]]
        .map((flag) => (flag ? "1" : "0"))
        .join(" ");

      const fetchedName = nameResp.successfull ? (nameResp.value || "") : "";

      const fromDT = convertToDatetimeLocal(validObj.from);
      const toDT   = convertToDatetimeLocal(validObj.to);

      setCardData({
        cardNumber: cardNumberVal.toString(),
        name: fetchedName,
        validFrom: fromDT, 
        validTo: toDT,
        pin: pinVal.toString(),
        doorFlags: doorFlagsStr,
      });

      const combined = {
        ...cardResp,
        info: cardResp.info,
        value: {
          ...cardResp.value,
          name: fetchedName,
        },
      };
      setResult(JSON.stringify(combined, null, 2));
      showSnackbar("Card fetched successfully.");
    } catch (err) {
      setResult(`Error: ${err.message}`);
    }
  }, [lookupCardNumber, callEndpoint, showSnackbar, convertToDatetimeLocal]);

  // 3) handleSaveCard
  const handleSaveCard = useCallback(async () => {
    if (!cardData.cardNumber) {
      showSnackbar("No card number to save. Fetch or enter a card first.");
      return;
    }
    try {
      // doorFlags -> array of bytes
      const doorFlagsArr = cardData.doorFlags.split(/\s+/).map((f) => {
        const val = parseInt(f, 10);
        return Number.isNaN(val) ? 0 : val;
      });

      const fromDate = convertFromDatetimeLocal(cardData.validFrom);
      const toDate   = convertFromDatetimeLocal(cardData.validTo);

      const putResp = await callEndpoint("putCard", {
        cardNumber: parseInt(cardData.cardNumber, 10),
        userName: cardData.name,
        from: fromDate,
        to: toDate,
        doorFlags: doorFlagsArr.join(" "),
        pin: parseInt(cardData.pin, 10),
      });

      setResult(JSON.stringify(putResp, null, 2));
      showSnackbar("Card saved successfully.");
    } catch (err) {
      setResult(`Error: ${err.message}`);
    }
  }, [cardData, callEndpoint, showSnackbar, convertFromDatetimeLocal]);

  return (
    <Box sx={{ p: 2 }}>
      <Typography variant="h4" gutterBottom>
        Card Manager
      </Typography>

      {/* LOOKUP SECTION */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Typography variant="h6">Fetch Card by Number</Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <TextField
              label="Card Number to Lookup"
              fullWidth
              value={lookupCardNumber}
              onChange={(e) => setLookupCardNumber(e.target.value)}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <Button variant="contained" onClick={handleFetchCard}>
              Load Card
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {/* EDIT SECTION */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Typography variant="h6">Edit Card Fields</Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={4}>
            <TextField
              label="Card Number"
              fullWidth
              value={cardData.cardNumber}
              onChange={(e) => setCardData((prev) => ({ ...prev, cardNumber: e.target.value }))}
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField
              label="Name"
              fullWidth
              value={cardData.name}
              onChange={(e) => setCardData((prev) => ({ ...prev, name: e.target.value }))}
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField
              label="PIN"
              fullWidth
              value={cardData.pin}
              onChange={(e) => setCardData((prev) => ({ ...prev, pin: e.target.value }))}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              type="datetime-local"
              label="Valid From"
              fullWidth
              InputLabelProps={{ shrink: true }}
              value={cardData.validFrom}
              onChange={(e) => setCardData((prev) => ({ ...prev, validFrom: e.target.value }))}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              type="datetime-local"
              label="Valid To"
              fullWidth
              InputLabelProps={{ shrink: true }}
              value={cardData.validTo}
              onChange={(e) => setCardData((prev) => ({ ...prev, validTo: e.target.value }))}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              label="Door Flags (e.g. '1 0 1 1')"
              fullWidth
              value={cardData.doorFlags}
              onChange={(e) => setCardData((prev) => ({ ...prev, doorFlags: e.target.value }))}
              helperText="0=No Access, 1=Has Access"
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <Button variant="contained" onClick={handleSaveCard}>
              Save Changes
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {/* RESPONSE DISPLAY */}
      <Paper sx={{ p: 2 }}>
        <Typography variant="h6">Response</Typography>
        <Box
          component="pre"
          sx={{ backgroundColor: "#f4f4f4", p: 2, whiteSpace: "pre-wrap" }}
        >
          {result}
        </Box>
      </Paper>

      {/* SNACKBAR */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity="info"
          sx={{ width: "100%" }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
}

export default CardManager;
